import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { NavigationBarButton } from "../../../screens/header/subcomponents/NavigationBarButton";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { enviroment } from "../../../../enviroment";
import { useCartContext } from "../../../../contexts/CartContext";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { getCartItemsCurrency } from "../../../screens/order-create/models/CartItem";
import PropTypes from "prop-types";
import { cartItemsProductPending } from "../../../../utils/functions/productPending";
import { CLARITY_UNMASK } from "../../../../utils/analytics/clarityAttrs";

export function CartNavMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, currency, organization, workshopMode } = useProfileContext();

  const { cartSummary, isUpdating, activeOrder, items } = useCartContext();
  const hasCartItems = (cartSummary?.totalQuantity ?? 0) > 0;
  const { pathname } = useLocation();
  // Cart fixes (Clarity: 280 dead clicks on the header cart while already on
  // the cart page): on /order/place the click scrolls to the top and pulses
  // the button for 600 ms instead of re-navigating to the same route.
  const onCartPage =
    enviroment.uxCartFixesEnabled && pathname === "/order/place";
  const [pulse, setPulse] = useState(false);
  const pulseTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(pulseTimer.current), []);

  function handleToCart() {
    if (onCartPage) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setPulse(true);
      clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulse(false), 600);
      return;
    }
    navigate("/order/place");
  }

  // Use active order total if available (e.g. on confirmation page with delivery/packaging fees),
  // otherwise fallback to cart summary total
  const displayTotal = activeOrder
    ? activeOrder.subTotal
    : cartSummary?.totalPrice;

  // The totals above are plain sums of the price rows' amounts (no FE
  // conversion), so the label must be the ROWS' currency — the profile
  // currency relabeled foreign amounts under act-as (EUR header on DKK
  // sums). The active order draft already carries the row-derived currency.
  const displayCurrency =
    activeOrder?.currency ?? getCartItemsCurrency(items, currency);

  // "Prices on their way" (flag productPendingEnabled): a total summed over
  // a line whose product is still owed by the API worker would be an old or
  // zero price - show the pending label instead. Always false with the flag
  // off.
  const productPending = cartItemsProductPending(items);

  if (role === Role.Admin) {
    return <AdminQueueNavMenu />;
  }

  if ((role === Role.EndUser && organization?.hidePrices) || workshopMode) {
    return null;
  }

  return (
    <div>
      <NavigationBarButton
        className={pulse ? "px-5 w-[200px] animate-pulse" : "px-5 w-[200px]"}
        onClick={handleToCart}
        {...(onCartPage ? { "aria-current": "page" as const } : {})}
      >
        <div className="flex justify-between space-x-4">
          <CartTypography className="content-center">
            {t("compMisc.items")}{" "}
            {isUpdating ? (
              <FontAwesomeIcon
                size={"lg"}
                icon={faSpinner}
                className="animate-spin"
              />
            ) : (
              // Clarity (design 5.3): the item count is unmasked; the total
              // amount below stays masked (a price).
              <b {...CLARITY_UNMASK}>{cartSummary?.totalQuantity}</b>
            )}
          </CartTypography>
          <CartTypography className="content-center">
            {t("compMisc.total")}{" "}
            {isUpdating ? (
              <FontAwesomeIcon
                size={"lg"}
                icon={faSpinner}
                className="animate-spin"
              />
            ) : productPending ? (
              <b>{t("product_pending")}</b>
            ) : (
              <b>{`${displayCurrency} ${displayTotal?.toFixed(2)}`}</b>
            )}
          </CartTypography>
          <div className={"content-center relative"}>
            {hasCartItems && (
              <div
                className={
                  "h-[7px] w-[7px] rounded-full bg-success absolute top-[-2px] -right-1"
                }
              />
            )}
            <FontAwesomeIcon size={"lg"} icon={faCartShopping} />
          </div>
        </div>
      </NavigationBarButton>
    </div>
  );
}

export const CartTypography = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>((props, ref) => {
  const { className, ...rest } = props;
  return (
    <p
      ref={ref}
      className={`text-xxs capitalize text-left ${className ?? ""}`}
      {...rest}
    >
      {props.children}
    </p>
  );
});

CartTypography.displayName = "CartTypography";
CartTypography.propTypes = {
  className: PropTypes.string,
};

function AdminQueueNavMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logoRepository, designRepository } = useRepositoriesContext();
  // React Query hooks: the header remounts several times during app startup, and the
  // previous imperative fetch fired on every mount (~5x per page load). The shared query
  // keys dedupe those into one request per queue, cached, still polling every 60s.
  const { data: logoCounts, isLoading: isLoadingLogos } =
    logoRepository.useImportLogosQueueCounts();
  const { data: designCounts, isLoading: isLoadingDesigns } =
    designRepository.useImportDesignsQueueCounts();
  const isLoading = isLoadingLogos || isLoadingDesigns;

  const map = new Map<string, number>();
  (logoCounts ?? []).forEach((c) => {
    const key = (c.queueName ?? "").toLowerCase();
    map.set(key, c.count ?? 0);
  });
  const importCount = map.get("import-logos-queue") ?? 0;
  const logoCheckCount = map.get("logo-check-endpoint") ?? 0;
  const designQueueCount = (designCounts ?? []).reduce(
    (total, queue) => total + (queue.count ?? 0),
    0
  );

  const counts = (
    <>
      <div className="px-4 flex justify-between space-x-2">
        <CartTypography className="content-center">
          {t("compMisc.logoCheckQueue")}
        </CartTypography>
        <CartTypography className="content-center">
          {isLoading ? (
            <FontAwesomeIcon
              size={"lg"}
              icon={faSpinner}
              className="animate-spin"
            />
          ) : (
            <b>{logoCheckCount}</b>
          )}
        </CartTypography>
      </div>
      <div className="px-4 flex justify-between space-x-2">
        <CartTypography className="content-center">
          {t("compMisc.logoQueue")}
        </CartTypography>
        <CartTypography className="content-center">
          {isLoading ? (
            <FontAwesomeIcon
              size={"lg"}
              icon={faSpinner}
              className="animate-spin"
            />
          ) : (
            <b>{importCount}</b>
          )}
        </CartTypography>
      </div>
      <div className="px-4 flex justify-between space-x-2">
        <CartTypography className="content-center">
          {t("compMisc.designQueue")}
        </CartTypography>
        <CartTypography className="content-center">
          {isLoading ? (
            <FontAwesomeIcon
              size={"lg"}
              icon={faSpinner}
              className="animate-spin"
            />
          ) : (
            <b>{designQueueCount}</b>
          )}
        </CartTypography>
      </div>
    </>
  );

  // Cart fixes: the admin's queue box is a button that opens the import
  // status page (Clarity: dead clicks on the counts). Off = today's plain div.
  if (enviroment.uxCartFixesEnabled) {
    return (
      <NavigationBarButton
        className="flex flex-col space-y-2 justify-center !w-auto"
        onClick={() => navigate("/admin/import-status")}
        title={t("ux.openImportStatus")}
      >
        {counts}
      </NavigationBarButton>
    );
  }

  return (
    <div className="flex flex-col space-y-2 justify-center">{counts}</div>
  );
}
